import { useState, useEffect, useCallback } from 'react';
import interviewService from '../services/interviewService';
import { Interview, InterviewCreate, InterviewUpdate } from '../types';
import { notify } from '../lib/notify';
import i18n from '../i18n/config';

interface UseInterviewsReturn {
  interviews: Interview[];
  isLoading: boolean;
  error: string | null;
  createInterview: (data: InterviewCreate) => Promise<Interview | null>;
  updateInterview: (interviewId: string, data: InterviewUpdate) => Promise<Interview | null>;
  deleteInterview: (interviewId: string) => Promise<boolean>;
  refreshInterviews: () => Promise<void>;
}

// Helper function to format error messages from API responses
const formatErrorMessage = (err: any, defaultMessage: string): string => {
  if (!err?.response?.data?.detail) {
    return err?.message || defaultMessage;
  }

  const detail = err.response.data.detail;

  // If detail is an array (FastAPI validation errors)
  if (Array.isArray(detail)) {
    return detail.map((error: any) => {
      const location = error.loc ? error.loc.join(' > ') : '';
      return `${location}: ${error.msg}`;
    }).join('; ');
  }

  // If detail is a string
  if (typeof detail === 'string') {
    return detail;
  }

  // Fallback
  return defaultMessage;
};

export const useInterviews = (domainId?: string, cadrageId?: string): UseInterviewsReturn => {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadInterviews = useCallback(async () => {
    if (!domainId && !cadrageId) {
      setError(i18n.t('errors.interview.domainOrCadrageRequired'));
      setInterviews([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await interviewService.getInterviews(domainId, cadrageId);
      setInterviews(data);
    } catch (err: any) {
      const errorMessage = formatErrorMessage(err, i18n.t('errors.interview.loadFailed'));
      setError(errorMessage);
      console.error('Error loading interviews:', err);
    } finally {
      setIsLoading(false);
    }
  }, [domainId, cadrageId]);

  useEffect(() => {
    loadInterviews();
  }, [loadInterviews]);

  const createInterview = async (data: InterviewCreate): Promise<Interview | null> => {
    try {
      const newInterview = await interviewService.createInterview(data);
      setInterviews(prev => [...prev, newInterview]);
      notify.show({
        variant: 'success',
        title: i18n.t('interview.toasts.createdTitle'),
        description: newInterview.title
          ? i18n.t('interview.toasts.createdNamed', { title: newInterview.title })
          : i18n.t('interview.toasts.created'),
        link: cadrageId
          ? `/cadrages/${cadrageId}`
          : domainId
          ? `/domains/${domainId}`
          : undefined,
      });
      return newInterview;
    } catch (err: any) {
      const errorMessage = formatErrorMessage(err, i18n.t('errors.interview.createFailed'));
      notify.error(errorMessage);
      console.error('Error creating interview:', err);
      return null;
    }
  };

  const updateInterview = async (interviewId: string, data: InterviewUpdate): Promise<Interview | null> => {
    try {
      const updatedInterview = await interviewService.updateInterview(interviewId, data);
      setInterviews(prev =>
        prev.map(interview => interview.id === interviewId ? updatedInterview : interview)
      );
      notify.show({
        variant: 'success',
        title: i18n.t('interview.toasts.updatedTitle'),
        description: updatedInterview.title
          ? i18n.t('interview.toasts.updatedNamed', { title: updatedInterview.title })
          : i18n.t('interview.toasts.updated'),
      });
      return updatedInterview;
    } catch (err: any) {
      const errorMessage = formatErrorMessage(err, i18n.t('errors.interview.updateFailed'));
      notify.error(errorMessage);
      console.error('Error updating interview:', err);
      return null;
    }
  };

  const deleteInterview = async (interviewId: string): Promise<boolean> => {
    try {
      await interviewService.deleteInterview(interviewId);
      setInterviews(prev => prev.filter(interview => interview.id !== interviewId));
      notify.show({
        variant: 'success',
        title: i18n.t('interview.toasts.deletedTitle'),
        description: i18n.t('interview.toasts.deleted'),
      });
      return true;
    } catch (err: any) {
      const errorMessage = formatErrorMessage(err, i18n.t('errors.interview.deleteFailed'));
      notify.error(errorMessage);
      console.error('Error deleting interview:', err);
      return false;
    }
  };

  return {
    interviews,
    isLoading,
    error,
    createInterview,
    updateInterview,
    deleteInterview,
    refreshInterviews: loadInterviews,
  };
};

